import { Divider, Switch, Text } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type { DurationMeasure } from '@/types';
import { UNLIMITED_VALUE } from '@/constants/unlimited';
import { TEST_IDS, TEST_ID_BUILDERS } from '@/testIds';
import { addS } from '@/shared/lib';
import { EditableSectionCard } from '../EditableSectionCard';
import { ChipSelect, type ChipOption } from '../ChipSelect';
import { NumberStepper } from './NumberStepper';
import styles from './DurationStepper.module.css';

type DurationFormShape = {
  durationNumber: string;
  durationMeasure: DurationMeasure;
};

const MEASURES: ChipOption<DurationMeasure>[] = [
  { value: 'Days', label: 'Days' },
  { value: 'Weeks', label: 'Weeks' },
  { value: 'Months', label: 'Months' },
  { value: 'Years', label: 'Years' },
];

const MIN = 1;
const MAX = 365;
const clamp = (n: number): number => Math.min(MAX, Math.max(MIN, n));
const formatDurationSummary = (
  value: string,
  measure: DurationMeasure,
  isUnlimited: boolean
): string => {
  if (isUnlimited) return 'No expiry';

  const measureName = measure.toLowerCase().replace(/s$/, '');
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return `— ${measureName}`;

  return `${parsed} ${addS(parsed, measureName)}`;
};

export const DurationStepper: FC = () => {
  const { control } = useFormContext<DurationFormShape>();

  const { field: numberField, fieldState: numberFieldState } = useController({
    name: 'durationNumber',
    control,
    rules: {
      validate: (value) => {
        if (value === UNLIMITED_VALUE) return true;
        const parsed = parseInt(value.trim(), 10);
        if (Number.isNaN(parsed)) return 'Enter a number';
        if (parsed <= 0) return 'Duration must be greater than 0';
        return true;
      },
    },
  });
  const { field: measureField } = useController({ name: 'durationMeasure', control });

  const isUnlimited = numberField.value === UNLIMITED_VALUE;
  const lastRef = useRef<string>(isUnlimited ? String(MIN) : numberField.value || String(MIN));
  const inputRef = useRef<HTMLInputElement>(null);
  const unlimitedToggleRef = useRef<HTMLInputElement>(null);
  if (!isUnlimited && numberField.value) {
    lastRef.current = numberField.value;
  }

  const current = isUnlimited
    ? parseInt(lastRef.current, 10) || MIN
    : parseInt(numberField.value, 10) || MIN;

  const setNumber = (next: number): void => {
    const clamped = clamp(next);
    lastRef.current = String(clamped);
    numberField.onChange(String(clamped));
  };

  const toggleUnlimited = (checked: boolean): void => {
    if (checked) {
      if (numberField.value !== UNLIMITED_VALUE) {
        lastRef.current = numberField.value || String(MIN);
      }
      numberField.onChange(UNLIMITED_VALUE);
    } else {
      numberField.onChange(lastRef.current || String(MIN));
    }
  };

  const summary = formatDurationSummary(numberField.value, measureField.value, isUnlimited);

  return (
    <EditableSectionCard
      label="Duration"
      summary={summary}
      focusTargetRef={isUnlimited ? unlimitedToggleRef : inputRef}
      invalid={numberFieldState.invalid}
      triggerTestId={TEST_IDS.durationEditorTrigger}
    >
      <NumberStepper
        value={numberField.value}
        unlimited={isUnlimited}
        decrementDisabled={current <= MIN}
        onInput={(next) => {
          if (next) lastRef.current = next;
          numberField.onChange(next);
        }}
        onStep={(delta) => setNumber(current + delta)}
        onBlur={() => {
          setNumber(current);
          numberField.onBlur();
        }}
        inputRef={inputRef}
        inputTestId={TEST_IDS.durationNumberInput}
        inputAriaLabel="Duration"
        decrementAriaLabel="Decrease duration"
        incrementAriaLabel="Increase duration"
      />

      <ChipSelect<DurationMeasure>
        options={MEASURES}
        value={measureField.value}
        onChange={measureField.onChange}
        ariaLabel="Duration unit"
        flush
        fill
        disabled={isUnlimited}
        chipTestId={(value) => TEST_ID_BUILDERS.durationMeasureChip(value)}
      />

      <Divider />

      <div className={styles.toggleRow}>
        <Text weight="regular">No expiry</Text>
        <Switch
          ref={unlimitedToggleRef}
          checked={isUnlimited}
          onChange={(e) => toggleUnlimited(e.target.checked)}
          aria-label="Toggle no expiry"
        />
      </div>
    </EditableSectionCard>
  );
};
